import { Alert, Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { default as React, useEffect, useState } from 'react';
import { ArrayInput, BooleanInput, DateInput, Edit, FormDataConsumer, NumberInput, ReferenceArrayInput, SelectArrayInput, SelectInput, SimpleForm, SimpleFormIterator, TextInput, minValue, required, useDataProvider } from 'react-admin';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import { FIELD_WIDTH, getDrawingRuleName, getLabelValue } from './DrawingCreate';
import { PriceInput, Tooltip } from '../../components';
import { AlterValueDrawing } from '../../models';
import AddCustomItemButton from '../../components/AddCustomItemButton';
import { MAX_DATE } from '../../constants';

const DrawingEdit = (props) => {
  const [drawingRules, setDrawingRules] = useState<any[]>([]);
  const [fuels, setFuels] = useState<any[]>([]);
  const dataProvider = useDataProvider();

  useEffect(() => {
    dataProvider.getList('fuels', { sort: { field: "name", order: "ASC" }, filter: null, pagination: { page: 1, perPage: 50 } })
      .then(({ data }) => setFuels(data));
  }, []);

  useEffect(() => {
    dataProvider.getList('drawing-rules', {
      sort: { field: "label", order: "ASC" },
      filter: null,
      pagination: { page: 1, perPage: 50 },
    })
      .then(({ data }) => setDrawingRules(data));
  }, []);

  return (
    <Edit
      {...props}
      title='Editar Sorteio'
      redirect="list"
      transform={({ drawingRuleFuels, ...data }) => {
        // Sorteio com regras via Clube de Vantagens não renderiza o input das regras
        // legadas — drawingRuleFuels chega undefined e a chave não vai no payload.
        if (!drawingRuleFuels) { return data; }
        return { ...data, drawingRuleFuels: drawingRuleFuels.map(({ drawingRule, ...rest }) => rest) };
      }}
    >
      <SimpleForm>
        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <TextInput
              label="Nome"
              source="name"
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.name } }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <DateInput
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.date } }}
              source="startDate"
              label="Data inicial"
              locales="pt-BR"
              inputProps={{ max: MAX_DATE }}
              parse={value => moment(value).startOf('day').toISOString()}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <DateInput
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.date } }}
              source="endDate"
              label="Data final"
              locales="pt-BR"
              inputProps={{ max: MAX_DATE }}
              parse={value => moment(value).endOf('day').toISOString()}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <DateTimeInputUTC
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.dateTime } }}
              source="drawingDate"
              label="Data e hora do sorteio"
              locales="pt-BR"
              validate={[required()]}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <NumberInput
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.number } }}
              source="startNumber"
              label="Número inicial"
              min={0}
              validate={[minValue(0)]}
              disabled
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <NumberInput
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.number } }}
              source="endNumber"
              label="Número final"
              min={1}
              validate={[minValue(1)]}
              disabled
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <NumberInput
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.number } }}
              source="winningNumber"
              label="Número Sorteado"
              min={0}
              validate={[minValue(0)]}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 'auto' }}>
            <TextInput
              source="drawingRegulationUrl"
              label="Url do regulamento"
              fullWidth={false}
              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.url } }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%', marginTop: 3 }}>
          <Grid size={12}>
            <BooleanInput
              source="isClubDriven"
              label="Regras via Clube de Vantagens"
              disabled
              helperText="Não pode ser alterado: trocar depois deixaria regra órfã ou faria o sorteio conceder número duas vezes."
            />
          </Grid>
        </Grid>

        <FormDataConsumer>
          {({ formData }) => formData.isClubDriven ? (
            <Alert severity="info" sx={{ width: '100%', marginTop: 2 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                As regras deste sorteio ficam no Clube de Vantagens
              </Typography>
              <Typography variant="body2">
                {'Este sorteio não tem regras próprias. Quem decide quem ganha número da sorte, quando e quantos é o Clube de Vantagens: a recompensa '}
                <strong>Número da Sorte</strong>
                {' do brinde é que aponta para este sorteio.'}
              </Typography>
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ width: '100%', marginTop: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Sorteio no modelo antigo
                </Typography>
                <Typography variant="body2">
                  {'As regras deste sorteio ficam aqui mesmo, e continuam funcionando — nada precisa ser refeito. Sorteios criados de agora em diante têm as regras no '}
                  <strong>Clube de Vantagens</strong>
                  {', com regra por compra na loja, limite por cliente, etiqueta e agendamento.'}
                </Typography>
              </Alert>
              <ArrayInput source="drawingRuleFuels" label="Regras do sorteio" style={{ marginTop: 50 }}>
              <SimpleFormIterator fullWidth disableReordering disableRemove={(record) => !!(record && record.id)} addButton={<AddCustomItemButton name="drawingRuleFuels" resetFields={{ luckyNumberAmount: 1 }} />}>
                <FormDataConsumer>
                  {({ scopedFormData }) => {
                    if (!scopedFormData) { return; }

                    const fuelChoices = [
                      { id: 'all', name: 'Todos' },
                      ...fuels
                    ];

                    const ruleId = scopedFormData.drawingRuleId;
                    const drawingRuleName = getDrawingRuleName(ruleId, drawingRules);

                    if (drawingRuleName === 'fillin-count') {
                      scopedFormData.accumulated = true;
                    } else if (drawingRuleName.includes('register-customer') && scopedFormData) {
                      scopedFormData.accumulated = false;
                      scopedFormData.value = 0;
                    } else if (drawingRules.length > 0 && !drawingRuleName.includes('fillin') && !drawingRuleName.includes('points-exchange')) {
                      scopedFormData.accumulated = null;
                      scopedFormData.value = 0;
                    } else if (drawingRuleName.includes('complete-customer-registration')) {
                      scopedFormData.accumulated = null;
                      scopedFormData.value = 0;
                    } else if (drawingRuleName.includes("fillin") && (scopedFormData.type === AlterValueDrawing.point || !scopedFormData.type)) {
                      scopedFormData.type = AlterValueDrawing.amount;
                    } else if (drawingRuleName.includes('points-exchange')) {
                      scopedFormData.type = AlterValueDrawing.point;
                    }

                    return (
                      <Box sx={{ marginTop: 2 }}>
                        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
                          <Grid size={{ xs: 12, sm: 'auto' }}>
                            <SelectInput
                              label="Regra"
                              fullWidth={false}
                              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.rule } }}
                              choices={drawingRules}
                              optionText="label"
                              source="drawingRuleId"
                              validate={[required()]}
                              record={scopedFormData}
                            />
                          </Grid>
                          {(scopedFormData?.drawingRuleId && drawingRuleName.includes('points-exchange')) &&
                            <Grid size={{ xs: 12, sm: 'auto' }}>
                              <TextInput
                                fullWidth={false}
                                sx={{ width: { xs: '100%', sm: FIELD_WIDTH.value } }}
                                source="value"
                                label="Valor em pontos"
                                defaultValue={0}
                                record={scopedFormData}
                                format={value => (!value ? '0,00' : (+value).toFixed(2))}
                                parse={value => (!value ? 0 : +value.replace(/\D/g, '') / 100)}
                              />
                            </Grid>
                          }
                          {scopedFormData?.drawingRuleId
                            && drawingRuleName.includes('fillin') && <>
                              <Grid size={{ xs: 12, sm: 'auto' }}>
                                <SelectInput
                                  label="Combustível"
                                  fullWidth={false}
                                  sx={{ width: { xs: '100%', sm: FIELD_WIDTH.fuel } }}
                                  choices={fuelChoices}
                                  source="fuelId"
                                  record={scopedFormData}
                                  defaultValue={null}
                                  format={value => value !== null ? value : 'all'}
                                  parse={value => value !== 'all' ? value : null}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 'auto' }}>
                                <SelectInput
                                  label="Tipo do valor"
                                  source="type"
                                  defaultValue={"amount"}
                                  fullWidth={false}
                                  sx={{ width: { xs: '100%', sm: FIELD_WIDTH.valueType } }}
                                  choices={[{ id: "amount", name: "Litros" }, { id: "value", name: "Valor" }]}
                                  validate={[required()]}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 'auto' }}>
                                {
                                  scopedFormData.type === AlterValueDrawing.value ?
                                    <PriceInput
                                      source="value"
                                      label='Valor'
                                      fullWidth={false}
                                      sx={{ width: { xs: '100%', sm: FIELD_WIDTH.value } }}
                                      validate={[minValue(0)]}
                                    />
                                    :
                                    <TextInput
                                      fullWidth={false}
                                      sx={{ width: { xs: '100%', sm: FIELD_WIDTH.value } }}
                                      source="value"
                                      label={getLabelValue(ruleId, drawingRules)}
                                      record={scopedFormData}
                                      defaultValue={0}
                                      validate={[minValue(0)]}
                                      format={value => drawingRuleName === 'fillin-count'
                                        ? (!value ? '1' : (+value).toFixed(0))
                                        : (!value ? '0,00' : (+value).toFixed(2))
                                      }
                                      parse={value => drawingRuleName === 'fillin-count'
                                        ? (!value ? 1 : +value.replace(/\D/g, ''))
                                        : (!value ? 0 : +value.replace(/\D/g, '') / 100)
                                      }
                                      required
                                    />
                                }
                              </Grid>
                            </>
                          }
                          {
                            (scopedFormData?.drawingRuleId && !drawingRuleName.includes('points-exchange')) &&
                            <Grid size={{ xs: 12, sm: 'auto' }}>
                              <TextInput
                                source="luckyNumberAmount"
                                label="Quantidade de cupons"
                                locales="pt-BR"
                                textAlign="center"
                                fullWidth={false}
                                sx={{ width: { xs: '100%', sm: FIELD_WIDTH.luckyNumberAmount } }}
                                defaultValue={1}
                                validate={[minValue(1)]}
                                format={value => (+value).toFixed(0)}
                                parse={value => +value.replace(/\D/g, '')}
                                required
                              />
                            </Grid>
                          }
                          <Grid size={{ xs: 12, sm: 'auto' }}>
                            <DateInput
                              fullWidth={false}
                              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.date } }}
                              source="startDate"
                              label="Data inicial"
                              locales="pt-BR"
                              inputProps={{ max: MAX_DATE }}
                              parse={value => moment(value).startOf('day').toISOString()}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 'auto' }}>
                            <DateInput
                              fullWidth={false}
                              sx={{ width: { xs: '100%', sm: FIELD_WIDTH.date } }}
                              source="endDate"
                              label="Data final"
                              locales="pt-BR"
                              inputProps={{ max: MAX_DATE }}
                              parse={value => moment(value).endOf('day').toISOString()}
                            />
                          </Grid>
                        </Grid>
                        <Grid container alignItems="center" spacing={2} sx={{ width: '100%' }}>
                          {
                            scopedFormData?.drawingRuleId
                            && drawingRuleName.includes('fillin') &&
                            <Grid size={{ xs: 12, sm: 'auto' }}>
                              <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Você pode indicar quais postos serão abrangidos pela regra. Se deixar em branco, todos os postos serão considerados válidos.">
                                <ReferenceArrayInput
                                  reference="stations"
                                  source="stationIds"
                                  perPage={null}
                                  sort={{ field: 'name', order: 'ASC' }}
                                >
                                  <SelectArrayInput
                                    label="Postos (Opcional)"
                                    optionText="name"
                                    placeholder="Todos"
                                    fullWidth={false}
                                    sx={{ width: { xs: '100%', sm: FIELD_WIDTH.stations } }}
                                  />
                                </ReferenceArrayInput>
                              </Tooltip>
                            </Grid>
                          }
                          {scopedFormData?.drawingRuleId
                            && drawingRuleName.includes('fillin') &&
                            <Grid size={{ xs: 12, sm: 'auto' }}>
                              <Tooltip sxContainer={{ alignItems: 'baseline' }} placement="top-start" title="Se esta opção estiver ativada, o cliente ganhará os números levando em conta todos os abastecimentos do perído do sorteio.">
                                <BooleanInput source="accumulated" label='Acumulado' defaultValue
                                  sx={{
                                    '& .MuiFormControlLabel-root': {
                                      mr: 1
                                    }
                                  }} disabled={drawingRuleName === 'fillin-count'} />
                              </Tooltip>
                            </Grid>
                          }
                          <Grid size={{ xs: 12, sm: 'auto' }}>
                            <BooleanInput source="isEnabled" defaultValue label="Regra ativada" sx={{ minWidth: 180 }} />
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  }}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
            </>
          )}
        </FormDataConsumer>
        <Typography variant="subtitle1" color="textSecondary" style={{ marginTop: 16, width: '100%' }}>
          * Os sorteios devem ser registrados na Caixa Econômica Federal e o registro é de responsabilidade do cliente.
        </Typography>
      </SimpleForm>
    </Edit>
  );
};

export default DrawingEdit;
